import { IDDocumentType } from "@amzn/client-workbench-api-model";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, {
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { BulletList } from "@/components/Commons/BulletList";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { STEP_ID } from "@/components/constants";
import { BackButton } from "@/components/IdentityCheck/BackButton";
import { getAiRetryContext } from "@/components/IdentityCheck/helper/rivHelper";
import ITChatVerificationGuidance from "@/components/IdentityCheck/ITChatVerificationGuidance";
import LightweightIdVerification from "@/components/IdentityCheck/LightweightIdVerification";
import PrimaryId from "@/components/IdentityCheck/PrimaryId";
import RecordStepContainer from "@/components/IdentityCheck/RecordStepContainer";
import SecondaryId from "@/components/IdentityCheck/SecondaryId";
import { VerificationOptionRow } from "@/components/IdentityCheck/VerificationOptionRow";
import { useShowChimeIdvGuidance } from "@/hooks/useShowChimeIdvGuidance";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

// SVG icons use hardcoded brand orange (#ff6200) since <img src> can't use CSS variables.
import IDCardIcon from "../../assets/icons/idcardicon.svg";
import PersonIcon from "../../assets/icons/personicon.svg";
import { useIVVAbort } from "./IVV/hooks/useIVVAbort";

interface RecordStep {
  /**
   * The type of the document that NHs will submit for this step.
   */
  documentType: IDDocumentType;

  /**
   * The i18n id of the step title.
   */
  stepI18nId: string;

  /**
   * Values prop that should be passed into i18n formatter
   */
  stepI18nValues?: Record<string, any>;

  /**
   * The i18n id of the button to render each record view.
   */
  expandBtnAltTextI18nId: string;

  /**
   * The view that will be rendered when NHs click into each step.
   */
  view: JSX.Element;

  /**
   * The Snowplow event label that will be sent through the Katal logger.
   */
  eventLabel:
    | Day1EventLabel.PrimaryId
    | Day1EventLabel.SecondaryId
    | Day1EventLabel.ChallengeAction;

  /** The icon to show in the step options */
  icon?: JSX.Element;
}

export const DATA_TEST_ID_FULL_VERIFICATION = "FullIdVerificationView";
export type RecordIdStepProps = {
  /**
   * The callback to clear the selected step and display the Next button and the list of FRIV steps
   */
  clearSelectedStep: () => void;

  /**
   * The reference to the FullIdVerification container element used to calculate the window
   * coordinate to scroll back to after the client completes ID recording steps.
   */
  fullIdVerificationElementRef: RefObject<HTMLDivElement>;
};

export type FullIdVerificationProps = {
  /**
   * Is user allowed to use automated IDV.
   */
  allowAutomatedIdvFallback?: boolean;
};

/**
 * Render a view that requires NHs to upload videos for their primary ID, secondary ID and challenge actions.
 * @constructor
 */
export default function FullIdVerification({
  allowAutomatedIdvFallback,
}: FullIdVerificationProps): JSX.Element {
  const intl = useIntl();
  const fontWeight = "regular";
  const [selectedRecordStep, setSelectedRecordStep] =
    useState<RecordStep | null>();
  const { state, dispatch } = useContext(AppContext);
  const showChimeIdvGuidance = useShowChimeIdvGuidance();
  const { ivvAborted } = useIVVAbort();

  /**
   * The back button on this component should only be shown if all of the following are true:
   * 1. No recordings have been selected (`!selectedRecordStep`)
   * 2. The user has not aborted IVV, if they have there's no other option (`ivvAborted`)
   * 3. The user is allowed to use automated IDV (`allowAutomatedIdvFallback`)
   *
   * Note: It's possible for `ivvAborted` and `allowAutomatedIdvFallback` to be out of sync
   * just after aborting IVV. Once `GetUserProgress` is refetched all the state will be updated
   * to mark IVV as 'OPT_OUT', but in the meantime to prevent the user from quickly clicking back
   * both conditions should be checked.
   */
  const backButtonEnabled =
    !selectedRecordStep && !ivvAborted && allowAutomatedIdvFallback;

  /**
   * Ref of this component used to calculate scroll window coordinate to scroll back to after
   * the client completes ID recording steps.
   */
  const fullIdVerificationElementRef = useRef<HTMLDivElement>(null);

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    updateOnboardingStep("D1_LOGIN");
    updateOnboardingStep("D1_IDV_START");

    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-identity-check-manual-full",
    });
  }, []);

  // When this view is rendered, set the non directed chat support context.
  useEffect(() => {
    // Next is hidden by default, however when RIV is selected, it should be enabled again.
    dispatch({
      type: ActionType.SET_HIDE_NEXT,
      hideNext: false,
      stepId: STEP_ID.IDENTITY_CHECK,
    });

    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext: "ON_FULL_RIV_TASK_SELECTION",
    });

    // Track when this section is opened for journey metrics.
    trackStructEvent({
      category: Day1EventCategory.IDCheckFull,
      action: Day1EventAction.SectionToggled,
    });
  }, []);

  /**
   * Show the next button by dispatching the SET_SHOW_NEXT action and clear to selected record step to render
   * the FRIV view again.
   */
  const clearSelectedStep = () => {
    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext: "ON_FULL_RIV_TASK_SELECTION",
    });

    dispatch({
      stepId: STEP_ID.IDENTITY_CHECK,
      type: ActionType.SET_HIDE_NEXT,
      hideNext: false,
    });
    setSelectedRecordStep(null);
  };

  const primaryStep: RecordStep = {
    documentType: "PRIMARY_ID_RECORDING",
    stepI18nId: "identityCheck-full-steps-recordPrimaryId",
    stepI18nValues: {
      requiredDocuments: state.requiredDocsWithGesturesCount - 1,
    },
    expandBtnAltTextI18nId: "identityCheck-full-primaryId-expandAltText",
    view: (
      <PrimaryId
        clearSelectedStep={clearSelectedStep}
        fullIdVerificationElementRef={fullIdVerificationElementRef}
      />
    ),
    eventLabel: Day1EventLabel.PrimaryId,
    icon: <img src={IDCardIcon} alt="ID card icon" />,
  };
  const secondaryStep: RecordStep = {
    documentType: "SECONDARY_ID_RECORDING",
    stepI18nId: "identityCheck-full-steps-recordSecondaryId",
    expandBtnAltTextI18nId: "identityCheck-full-secondaryId-expandAltText",
    view: (
      <SecondaryId
        clearSelectedStep={clearSelectedStep}
        fullIdVerificationElementRef={fullIdVerificationElementRef}
      />
    ),
    eventLabel: Day1EventLabel.SecondaryId,
    icon: <img src={IDCardIcon} alt="ID card icon" />,
  };
  const livenessStep: RecordStep = {
    documentType: "LIVENESS_RECORDING",
    stepI18nId: "identityCheck-full-steps-recordChallengeActions",
    expandBtnAltTextI18nId: "identityCheck-full-challengeActions-expandAltText",
    view: (
      <LightweightIdVerification
        eventCategory={Day1EventCategory.IDCheckFullChallenge}
      />
    ),
    eventLabel: Day1EventLabel.ChallengeAction,
    icon: <img src={PersonIcon} alt="Person icon" />,
  };
  const collectedInfoI18nIds = [
    "identityCheck-expander-body-whatIsCollected-item1",
    "identityCheck-expander-body-whatIsCollected-item2",
  ] satisfies string[];

  const aiRetryContext = getAiRetryContext();
  let initialRecordSteps: RecordStep[];
  if (aiRetryContext) {
    const catalog: RecordStep[] = [primaryStep, secondaryStep, livenessStep];
    initialRecordSteps = catalog.filter((step) =>
      aiRetryContext.documentTypes.includes(step.documentType)
    );
  } else {
    initialRecordSteps = [primaryStep, livenessStep];
    if (state.requiredDocsWithGesturesCount >= 3) {
      initialRecordSteps.splice(1, 0, secondaryStep);
    }
  }

  const [recordSteps] = useState<RecordStep[]>(initialRecordSteps);

  /**
   * Hide the next button by dispatching the SET_SHOW_NEXT action and set the select record step.
   * @param idx The index of the selected record step.
   */
  const updateRecordView = (idx: number) => {
    const recordStep = recordSteps[idx];

    switch (recordStep.documentType) {
      case "PRIMARY_ID_RECORDING":
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_PRIMARY_ID_STEP",
        });
        break;

      case "SECONDARY_ID_RECORDING":
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_SECONDARY_ID_STEP",
        });
        break;

      case "LIVENESS_RECORDING":
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_CHALLENGE_STEP",
        });
        break;
      default:
        /* istanbul ignore next: unreachable code */
        break;
    }

    trackStructEvent({
      category: Day1EventCategory.IDCheckFull,
      action: Day1EventAction.ClickRivSection,
      label: recordStep.eventLabel,
    });

    dispatch({
      stepId: STEP_ID.IDENTITY_CHECK,
      type: ActionType.SET_HIDE_NEXT,
      hideNext: true,
    });
    setSelectedRecordStep(recordStep);
  };

  /**
   * Render all steps required for FRIV.
   */
  const renderSteps = (): JSX.Element[] => {
    // Find the step they should click on next, it's the first step without an associated document
    const activeStep = recordSteps.findIndex(
      (step) =>
        !state.documents.find((doc) => step.documentType === doc.documentType)
    );
    return recordSteps.map((step, idx) => {
      const docIdx = state.documents.findIndex(
        (doc) => doc.documentType === step.documentType && doc.submitClicked
      );

      // Ensure NHs have clicked the submit button for each doc.
      const isSubmitBtnClicked = docIdx >= 0;
      // Ensure NHs have selected a primary ID issuing country and type from the select dropdowns.
      const isPrimaryIdSelectionMissing =
        step.documentType === "PRIMARY_ID_RECORDING" &&
        (!state.primaryIdSelection?.issuingCountry ||
          !state.primaryIdSelection?.documentType);
      const stepCompleted = isSubmitBtnClicked && !isPrimaryIdSelectionMissing;
      return (
        <VerificationOptionRow
          intlProvider={intl}
          optionIndex={idx}
          stepOnClick={stepCompleted ? undefined : () => updateRecordView(idx)}
          stepi18nId={step.stepI18nId}
          stepI18nValues={step.stepI18nValues}
          key={step.stepI18nId}
          stepCompleted={stepCompleted}
          stepIcon={step.icon}
          expandBtnIconAlti18nId={step.expandBtnAltTextI18nId}
          disabled={idx > activeStep}
        />
      );
    });
  };

  return (
    <div data-test-id={DATA_TEST_ID_FULL_VERIFICATION}>
      {backButtonEnabled && (
        <BackButton
          buttonText={intl.formatMessage({ id: "button-back" })}
          dataTestId="manualIdvBackBtn"
          buttonOnClickHandler={() =>
            dispatch({
              type: ActionType.SET_WIZARD_PATH,
              wizardPath: state.wizardPath.previous,
            })
          }
        />
      )}
      <Col gridGap="S300" ref={fullIdVerificationElementRef}>
        {selectedRecordStep ? (
          <RecordStepContainer
            eventLabel={selectedRecordStep.eventLabel}
            clearSelectedStep={clearSelectedStep}
          >
            {selectedRecordStep.view}
          </RecordStepContainer>
        ) : (
          <Col gridGap="S400">
            <StepBodyStyledText>
              <Text fontWeight="bold" dataTestId="idFullHeader">
                <FormattedMessage id="identityCheck-expander-body-header" />
              </Text>
              <Text fontWeight={fontWeight} dataTestId="idFullIntro">
                <FormattedMessage id="identityCheck-expander-body-intro" />
              </Text>
            </StepBodyStyledText>
            <View
              padding="S400"
              backgroundColor="color.surface.bg-default"
              dataTestId="recordInfoView"
              overflow="auto"
              height="250px"
            >
              <Col gridGap="S300">
                <Text fontWeight="medium">
                  <FormattedMessage id="identityCheck-expander-body-recordInfo-title" />
                </Text>
                <Text fontSize="T200" fontWeight="regular">
                  <FormattedMessage id="identityCheck-expander-body-recordInfo-body" />
                </Text>
                <Col gridGap="S100">
                  <Text
                    fontWeight="medium"
                    textDecoration={LinkTextDecorationOptions.Underline}
                  >
                    <FormattedMessage id="identityCheck-expander-body-whatIsCollected-title" />
                  </Text>
                  <Text fontSize="T200" fontWeight="regular">
                    <FormattedMessage id="identityCheck-expander-body-whatIsCollected-intro" />
                  </Text>
                  <BulletList
                    fontSize="T200"
                    fontWeight="regular"
                    i18nIds={collectedInfoI18nIds}
                  />
                  <Text fontSize="T200" fontWeight="regular">
                    <FormattedMessage id="identityCheck-expander-body-whatIsCollected-purpose" />
                  </Text>
                </Col>
              </Col>
            </View>
            {renderSteps()}
            {showChimeIdvGuidance && (
              <ITChatVerificationGuidance
                eventCategory={Day1EventCategory.IDCheckFull}
              />
            )}
          </Col>
        )}
      </Col>
    </div>
  );
}
